import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { client, handleToastError } from '../../utils/api-client';
import React from 'react';
import {
  GhgSupplierSurveyAnswerInSchema,
  GhgSupplierSurveyGetTokenSchema,
  GhgSupplierSurveySchema,
  GhgSupplierSurveyWithTokenSchema,
  SupplierQuestionnaireTypes,
} from '../interfaces';
import { QuestionnaireAnswer } from '../../views/components/QuestionnaireV3/QuestionnaireAnswer/index.types';
import { paths } from '../../views/components/QuestionnaireV3/helpers';

// eslint-disable-next-line import/prefer-default-export
export const useSupplierSurvey = () => {
  const { token } = useParams();
  const endpointUrl = `/web/ghg-suppliers/survey/answer/${token}`;
  const query = useQuery(['supplier_survey', token], () =>
    client
      .get<GhgSupplierSurveyWithTokenSchema>(endpointUrl)
      .then((response) => response.data)
  );

  const update = useMutation({
    mutationFn: (data: GhgSupplierSurveyAnswerInSchema) =>
      client.put<GhgSupplierSurveyWithTokenSchema>(endpointUrl, data),
    onSuccess: () => query.refetch(),
  });

  const submit = useMutation({
    mutationFn: (questionnaireType: SupplierQuestionnaireTypes) =>
      client.post(`${endpointUrl}/submit`, {
        questionnaire_type: questionnaireType,
      }),
    onSuccess: () => query.refetch(),
  });

  const fetchQv3TokenQuestionnaireEsg = React.useCallback(async () => {
    const response = await query.refetch();
    return response.data?.answer?.qv3_token_questionnaire_esg || '';
  }, [query.refetch, token]);

  const fetchQv3TokenQuestionnaireEnvironment = React.useCallback(async () => {
    const response = await query.refetch();
    return response.data?.answer?.qv3_token_questionnaire_environment || '';
  }, [query.refetch, token]);

  return {
    ...query,
    update,
    submit,
    fetchQv3TokenQuestionnaireEsg,
    fetchQv3TokenQuestionnaireEnvironment,
  };
};

export const qv3CheckRequiredQuestions = async (
  tokenAuth: () => Promise<string | undefined>,
  questionnaireAnswerId: string
) => {
  try {
    const token = await tokenAuth();
    if (!token) return false;
    const response = await client.get<QuestionnaireAnswer>(
      `${paths.questionnaireAnswers}/${questionnaireAnswerId}`,
      {
        headers: {
          'X-Questionnaires-Token': token,
        },
      }
    );
    for (const question of Object.values(
      response.data.questionnaire.questions
    )) {
      if (
        question.required &&
        !Object.keys(question.questionanswers || {}).length
      ) {
        return false;
      }
    }
    return true;
  } catch (err) {
    handleToastError(err);
  }
  return false;
};

export const useSupplierSurveyRaben = () => {
  const { token } = useParams();
  const isUuid =
    typeof token === 'string' &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      token
    );
  const endpointUrl = `/web/ghg-suppliers/survey-raben/${token}`;

  const query = useQuery(
    ['supplier_survey_raben', token],
    () =>
      client
        .get<GhgSupplierSurveySchema>(endpointUrl)
        .then((response) => response.data),
    { enabled: !!token && isUuid }
  );

  const getSurveyToken = useMutation({
    mutationFn: ({
      vatIdentifier,
      acceptMatch,
      reportingPeriod,
    }: {
      vatIdentifier: string;
      acceptMatch: boolean;
      reportingPeriod: string;
    }) =>
      client
        .get<GhgSupplierSurveyGetTokenSchema>(
          `${endpointUrl}/get-survey-access-token`,
          {
            params: {
              vat_identifier: vatIdentifier,
              accept_match: acceptMatch,
              reporting_period: reportingPeriod,
            },
          }
        )
        .then((response) => response.data),
  });

  return {
    ...query,
    getSurveyToken,
  };
};
